import { InputAddressData } from '@afterpaytouch/core'
import { Country, ErrorCode } from '@afterpaytouch/portal-api/types'
import { IsBiometricLoginEnabledPayload } from '../consumer/biometric/types'

export enum IdentityVerificationDocument {
  LICENCE = 'DRIVERS_LICENCE',
  MEDICARE = 'MEDICARE',
  PASSPORT = 'PASSPORT',
  PROFILE = 'PROFILE',
  GENERIC = 'GENERIC',
  PEPS_AND_SANCTIONS = 'PEPS_AND_SANCTIONS',
}

export enum TransformedIdentityVerificationDocument {
  LICENCE = 'licence',
  MEDICARE = 'medicare',
  PASSPORT = 'passport',
  PROFILE = 'profile',
  GENERIC = 'generic',
  PEPS_AND_SANCTIONS = 'peps_and_sanctions',
}

export enum IdentityStatus {
  VERIFIED = 'VERIFIED',
  REJECTED = 'REJECTED',
}

export enum IdentityCheckStatus {
  NOT_REQUIRED = 'NOT_REQUIRED',
  NOT_VERIFIED = 'NOT_VERIFIED',
  VERIFIED = 'VERIFIED',
  FAILED = 'FAILED',
  PARTIAL_VERIFIED = 'PARTIAL_VERIFIED',
}

export interface IdentityCheckStatuses {
  PBI: IdentityCheckStatus
  PCL: IdentityCheckStatus
}

export enum Channel {
  UNKNOWN = 'UNKNOWN',
  MOBILE = 'MOBILE',
  PORTAL = 'PORTAL',
  CHECKOUT = 'CHECKOUT',
  ADMIN = 'ADMIN',
}

export enum IdentityDocumentVerificationStatus {
  REJECTED = 'REJECTED',
  VERIFIED = 'VERIFIED',
}

export interface DocumentVerificationResult {
  type: IdentityVerificationDocument
  status: IdentityDocumentVerificationStatus
}

export interface IdentityVerifyResponse {
  identityCheckMetadata: IdentityCheckMetadata
  identityCheckStatus: IdentityCheckStatus
}

export interface IdentityCheckMetadata {
  numberOfProfileCheckAttemptsLeft: number
  profileCheckAttemptsLeft: boolean
  shouldPerformCreditHeaderCheck: boolean
}

export enum BiometricCheckProgress {
  NOT_STARTED = 'NOT_STARTED',
  CHECK_SUBMITTED = 'CHECK_SUBMITTED',
  CHECK_COMPLETED = 'CHECK_COMPLETED',
  CHECK_TERMINATED = 'CHECK_TERMINATED',
}

export interface IdentityNationalIDNumberResponse {
  identityStatus: IdentityStatus
}

export interface IdentityNationalIDNumberError {
  status: number
  data: {
    errorCode: ErrorCode
  }
}

export interface IdentityPassportRequest {
  channel: Channel
  countryCode: Country
  dateOfBirth: string
  givenNames: string
  number: string
  otherGivenNames?: string
  surname: string
  expiryDate?: string
  creditPolicyAgree?: boolean
}

export interface IdentityPassportResponse {
  status: IdentityDocumentVerificationStatus
}

export interface IdentityPassportCountries {
  [key: string]: string
}

export type IdentityPassportCountriesResponse = IdentityPassportCountries

export enum IdentityMedicareColour {
  GREEN = 'GREEN',
  BLUE = 'BLUE',
  YELLOW = 'YELLOW',
}

export interface IdentityMedicareRequest {
  channel: Channel
  number?: string
  reference?: string
  name: string
  name2?: string
  expiryDate: string
  colour: IdentityMedicareColour
  dateOfBirth: string
  creditPolicyAgree?: boolean
}

export interface IdentityMedicareResponse {
  status: IdentityDocumentVerificationStatus
}

export interface IdentityLicenseRequest {
  channel: Channel
  givenNames: string
  otherGivenNames?: string
  surname: string
  dateOfBirth: string
  licenceNumber: string
  licenceVersion?: string
  countryCode: Country
  state: string
  creditPolicyAgree?: boolean
  dlCardNumber?: string
}

export interface IdentityLicenseResponse {
  status: IdentityDocumentVerificationStatus
}

export interface IdentityVerifyRequest {
  channel?: Channel
}

interface IdentityNationalIDNumberBase {
  channel?: Channel
  address: InputAddressData
  givenNames: string
  otherGivenNames?: string
  surname: string
  dateOfBirth: string
}

interface IdentityPartialNationalIDNumber extends IdentityNationalIDNumberBase {
  partialNationalIdNumber: string
}

interface IdentityFullNationalIDNumber extends IdentityNationalIDNumberBase {
  fullNationalIdNumber: string
}

export type IdentityNationalIDNumberRequest = IdentityPartialNationalIDNumber | IdentityFullNationalIDNumber

export type OtpRouteOtpLogin = (isTrustedSession: boolean, biometricLoginEnabledCheckPayload: IsBiometricLoginEnabledPayload | boolean) => boolean
